import { DocumentTextIcon, TrashIcon } from '@heroicons/react/24/outline'
import {
  PackagingJob,
  JOB_STATUS_LABELS,
  JOB_STATUS_TONES,
  jobDisplayName,
  canCancelJob,
  canStopJob,
  canRetryJob,
  retryRunning,
  canDeleteJob,
} from '../types/job'
import { Badge, Spinner } from './ui'
import { dateTime, elapsed } from '../utils/date'
import { errorSummary } from '../utils/jobError'

interface JobCardProps {
  job: PackagingJob
  onSelect: (job: PackagingJob) => void
  onCancel?: (jobId: string) => void
  onStop?: (jobId: string) => void
  /** Re-runs a FAILED job. The page has always passed one; without the declaration React
   *  dropped it and the button below never existed. */
  onRetry?: (jobId: string) => void
  onDelete?: (jobId: string) => void
}

/**
 * ONE job, as the reference lists them (sap-mockup/app/(console)/jobs/page.tsx): a full-width row
 * reading name -> state -> timestamps -> actions, not a tile in a grid. A queue is read down the
 * left edge, which a three-column grid of cards makes impossible.
 *
 * Opening the details is an explicit `Logs` button rather than a click anywhere on the row: the
 * row carries four other controls, and a labelled button is reachable from the keyboard where a
 * clickable div is not.
 */
export default function JobCard({ job, onSelect, onCancel, onStop, onRetry, onDelete }: JobCardProps) {
  const took = elapsed(job.started_at ?? job.created_at, job.completed_at)

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-mono text-sm font-semibold text-ftext">
              {jobDisplayName(job)}
            </span>
            {job.status === 'processing' && (
              <Spinner className="h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-300" />
            )}
          </div>
          <span className="text-[12px] text-ffaint">{job.job_id.substring(0, 8)}…</span>
        </div>

        <div className="flex items-center gap-1.5">
          <Badge tone={JOB_STATUS_TONES[job.status]}>{JOB_STATUS_LABELS[job.status]}</Badge>
          {job.deployment_methods.map((method) => (
            <span
              key={method}
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                method === 'intune'
                  ? 'bg-blue-500/10 text-blue-600 dark:text-blue-300'
                  : 'bg-panel3 text-fmuted'
              }`}
            >
              {method}
            </span>
          ))}
        </div>

        <div className="hidden text-right text-[12px] text-fmuted sm:block">
          <div>Created: {dateTime(job.created_at)}</div>
          {/* A finished job is stamped with the state it finished IN — "Completed:" over a
              failure would misreport it. */}
          <div>
            {job.completed_at
              ? `${JOB_STATUS_LABELS[job.status]}: ${dateTime(job.completed_at)}`
              : job.status === 'processing'
                ? 'Running…'
                : '—'}
          </div>
        </div>

        {took && <div className="hidden text-xs text-ffaint lg:block">{took}</div>}

        <button onClick={() => onSelect(job)} className="btn-ghost btn-sm">
          <DocumentTextIcon className="h-4 w-4" />
          Logs
        </button>

        {canCancelJob(job.status) && onCancel && (
          <button
            onClick={() => onCancel(job.job_id)}
            className="btn-ghost btn-sm text-amber-600 hover:bg-amber-500/10 hover:text-amber-600 dark:text-amber-300 dark:hover:text-amber-300"
          >
            Cancel
          </button>
        )}
        {canStopJob(job.status) && !canCancelJob(job.status) && onStop && (
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to stop this job? This will forcefully terminate it.')) {
                onStop(job.job_id)
              }
            }}
            className="btn-ghost btn-sm text-red-600 hover:bg-red-500/10 hover:text-red-600 dark:text-red-300 dark:hover:text-red-300"
          >
            Stop
          </button>
        )}
        {canRetryJob(job.status) && onRetry && !retryRunning(job) && (
          <button
            onClick={() => onRetry(job.job_id)}
            className="btn-ghost btn-sm text-accent hover:bg-accent/10 hover:text-accent"
          >
            Retry
          </button>
        )}
        {canDeleteJob(job.status) && onDelete && (
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this job and all its artifacts? This cannot be undone.')) {
                onDelete(job.job_id)
              }
            }}
            title="Delete job"
            aria-label="Delete job"
            className="rounded-md p-1.5 text-fmuted transition-colors hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-300"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Progress and error sit under the row rather than in it: both are only sometimes there,
          and a column that is usually blank pushes the timestamps out of alignment. Only a job
          that is actually running gets a bar — an empty track under a queued job reads as a
          stalled one. */}
      {job.status === 'processing' && (
        <div className="mt-3 flex items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-panel3">
            <div
              className="h-full rounded-full bg-accent transition-all duration-300"
              style={{ width: `${job.progress_pct}%` }}
            />
          </div>
          <span className="text-[11px] tabular-nums text-ffaint">{job.progress_pct}%</span>
        </div>
      )}

      {/* The cause only. The message carries the whole log tail, and pasting that into the row
          buries the queue under retry chatter — the full text is in the job's details. Only a
          failed job's message is an error: a queued job's says what it is waiting for. */}
      {job.error_message && (
        <p
          className={`mt-2 truncate text-[12px] ${
            job.status === 'failed' ? 'text-red-600 dark:text-red-300' : 'text-fmuted'
          }`}
          title={job.error_message}
        >
          {errorSummary(job.error_message)}
        </p>
      )}
    </div>
  )
}
